import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Dashboard from './pages/Dashboard/Dashboard';
import Customers from './pages/Customers/Customers';
import Installations from './pages/Installations/Installations';
import InstallationDetail from './pages/Installations/InstallationDetail';
import Services from './pages/Services/Services';
import ServiceRequestDetail from './pages/Services/ServiceRequestDetail';
import Chat from './pages/Messages/Messages';
import Scheduling from './pages/Scheduling/Scheduling';
import Reports from './pages/Reports/Reports';
import Settings from './pages/Settings/Settings';
import ProtectedRoute from './components/ProtectedRoute';

// Create dark theme matching Bosso's current website
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37', // Golden yellow from Bosso website
      light: '#E6C547',
      dark: '#B8941F',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FFFFFF', // White
      light: '#F5F5F5',
      dark: '#E0E0E0',
      contrastText: '#000000',
    },
    background: {
      default: '#1A1A1A', // Dark charcoal gray
      paper: '#2C2C2C', // Darker gray for cards
    },
    text: {
      primary: '#FFFFFF', // White
      secondary: '#AAAAAA', // Light gray
    },
    divider: '#333333',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      color: '#D4AF37', // Golden yellow
      fontWeight: 700,
    },
    h2: {
      color: '#D4AF37', // Golden yellow
      fontWeight: 600,
    },
    h3: {
      color: '#D4AF37', // Golden yellow
      fontWeight: 600,
    },
    h4: {
      color: '#D4AF37', // Golden yellow
      fontWeight: 500,
    },
    h5: {
      color: '#FFFFFF', // White
      fontWeight: 500,
    },
    h6: {
      color: '#FFFFFF', // White
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          backgroundColor: '#D4AF37', // Golden yellow
          color: '#000000',
          '&:hover': {
            backgroundColor: '#B8941F', // Darker golden yellow
          },
        },
        outlined: {
          borderColor: '#D4AF37', // Golden yellow
          color: '#D4AF37',
          '&:hover': {
            borderColor: '#B8941F',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C', // Darker gray for cards
          border: '1px solid #333333',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C', // Darker gray for papers
          border: '1px solid #333333',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A', // Dark charcoal gray
          borderBottom: '1px solid #333333',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A', // Dark charcoal gray
          borderRight: '1px solid #333333',
        },
      },
    },
  },
});

function App() {
  console.log('App component rendering...');
  
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="customers" element={<Customers />} />
                <Route path="installs" element={<Installations />} />
                <Route path="installs/:id" element={<InstallationDetail />} />
                <Route path="services" element={<Services />} />
                <Route path="services/:id" element={<ServiceRequestDetail />} />
                <Route path="chat" element={<Chat />} />
                <Route path="scheduling" element={<Scheduling />} />
                <Route path="reports" element={<Reports />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;